/**
 * Owner-only project actions (archive / unarchive / delete) as a dropdown +
 * confirmation dialogs. Shared by the project card (tile view) and the project
 * table rows so both surfaces get identical, single-sourced behavior.
 *
 * Renders nothing for non-owners. Manages its own dialog state and mutations.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@mono/ui-components';
import { Archive, ArchiveRestore, MoreVertical, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useArchiveProject, useDeleteProject, useUnarchiveProject } from '../../hooks/useProjects';
import { cn } from '../../lib/utils';
export function ProjectActionsMenu({ project, triggerClassName }) {
    const navigate = useNavigate();
    const [showArchiveDialog, setShowArchiveDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const archiveProject = useArchiveProject();
    const unarchiveProject = useUnarchiveProject();
    const deleteProject = useDeleteProject();
    const isOwner = project.role === 'owner';
    const isArchived = project.status === 'archived';
    const mutating = archiveProject.isPending || unarchiveProject.isPending || deleteProject.isPending;
    if (!isOwner) return null;
    const handleArchiveClick = async ()=>{
        try {
            await archiveProject.mutateAsync(project.projectId);
            toast.success('Project archived successfully');
            setShowArchiveDialog(false);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to archive project');
        }
    };
    const handleUnarchiveClick = async ()=>{
        try {
            await unarchiveProject.mutateAsync(project.projectId);
            toast.success('Project unarchived successfully');
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to unarchive project');
        }
    };
    const handleDeleteClick = async ()=>{
        try {
            await deleteProject.mutateAsync(project.projectId);
            toast.success('Project deleted permanently');
            setShowDeleteDialog(false);
            navigate('/projects');
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to delete project');
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(DropdownMenu, {
                children: [
                    /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            className: cn('p-1 rounded-md hover:bg-accent transition-opacity', triggerClassName),
                            onClick: (e)=>e.preventDefault(),
                            "aria-label": "Project actions",
                            disabled: mutating,
                            children: /*#__PURE__*/ _jsx(MoreVertical, {
                                className: "w-3.5 h-3.5 text-muted-foreground"
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                        align: "end",
                        className: "w-48",
                        children: [
                            isArchived ? /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                onClick: (e)=>{
                                    e.preventDefault();
                                    handleUnarchiveClick();
                                },
                                disabled: unarchiveProject.isPending,
                                className: "cursor-pointer",
                                children: [
                                    /*#__PURE__*/ _jsx(ArchiveRestore, {
                                        className: "w-4 h-4 mr-2"
                                    }),
                                    "Unarchive Project"
                                ]
                            }) : /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                onClick: (e)=>{
                                    e.preventDefault();
                                    setShowArchiveDialog(true);
                                },
                                disabled: archiveProject.isPending,
                                className: "cursor-pointer text-warning focus:text-warning focus:bg-warning-muted",
                                children: [
                                    /*#__PURE__*/ _jsx(Archive, {
                                        className: "w-4 h-4 mr-2"
                                    }),
                                    "Archive Project"
                                ]
                            }),
                            /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}),
                            /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                onClick: (e)=>{
                                    e.preventDefault();
                                    setShowDeleteDialog(true);
                                },
                                disabled: deleteProject.isPending,
                                className: "cursor-pointer text-danger focus:text-danger focus:bg-danger-muted",
                                children: [
                                    /*#__PURE__*/ _jsx(Trash2, {
                                        className: "w-4 h-4 mr-2"
                                    }),
                                    "Delete Permanently"
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(AlertDialog, {
                open: showArchiveDialog,
                onOpenChange: setShowArchiveDialog,
                children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogTitle, {
                                    children: "Archive Project?"
                                }),
                                /*#__PURE__*/ _jsxs(AlertDialogDescription, {
                                    children: [
                                        "This will archive ",
                                        /*#__PURE__*/ _jsx("strong", {
                                            children: project.name
                                        }),
                                        " and hide it from the main projects list. Files and chats will be preserved but editing will be disabled. You can restore it later from the archived projects list."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogCancel, {
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogAction, {
                                    onClick: handleArchiveClick,
                                    className: "bg-warning text-warning-foreground hover:bg-warning/90",
                                    children: "Archive Project"
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(AlertDialog, {
                open: showDeleteDialog,
                onOpenChange: setShowDeleteDialog,
                children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogTitle, {
                                    children: "Delete Project Permanently?"
                                }),
                                /*#__PURE__*/ _jsxs(AlertDialogDescription, {
                                    children: [
                                        "This will ",
                                        /*#__PURE__*/ _jsx("strong", {
                                            children: "permanently delete"
                                        }),
                                        " ",
                                        /*#__PURE__*/ _jsx("strong", {
                                            children: project.name
                                        }),
                                        " and all associated data including files, chats, and team members. This action cannot be undone."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogCancel, {
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogAction, {
                                    onClick: handleDeleteClick,
                                    className: "bg-danger text-danger-foreground hover:bg-danger/90",
                                    children: "Delete Permanently"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
